// ---------------------------------------------------------------------------
// loopSnap — pin loop waypoints to roads people are actually allowed to
// use (Morgan: "this is taking you on a private road" … "just make sure
// it doesn't exclude walking trails for walking mode").
//
// buildLoopPlan places waypoints geometrically; the router then snaps
// them to WHATEVER is nearest — including private drives. This module
// asks our own Overpass server what's really near each waypoint and
// moves it onto the closest way that's legal for the travel mode:
//
//   driving → public drivable roads (service/driveways excluded; named
//             roads preferred so the loop aims at real streets)
//   walking → all of the above quiet roads PLUS footways, paths, trails,
//             tracks, pedestrian streets (motorways excluded, obviously)
//   biking  → drivable roads (no motorways) plus cycleways/tracks/paths
//
// access=private/no is filtered server-side; per-mode bans (foot=no,
// bicycle=no, motor_vehicle=no) are filtered here. Fail-open: any error
// returns the original waypoints — a geometric loop still beats none.
// ---------------------------------------------------------------------------
import type { LngLat } from "@/utils/polyline";

type Mode = "driving" | "walking" | "biking";

const DRIVE_CLASSES = new Set([
  "trunk", "primary", "secondary", "tertiary", "unclassified",
  "residential", "living_street",
  "trunk_link", "primary_link", "secondary_link", "tertiary_link",
]);
const WALK_EXTRA = new Set([
  "footway", "path", "pedestrian", "track", "cycleway", "bridleway", "steps",
]);
const BIKE_EXTRA = new Set(["cycleway", "track", "path"]);

interface WayEl {
  type?: string;
  tags?: Record<string, string>;
  geometry?: Array<{ lat: number; lon: number }>;
}

/** Is this way usable by this mode? Exported for tests. */
export function wayEligible(tags: Record<string, string>, mode: Mode): boolean {
  const hw = tags.highway ?? "";
  const banned = (v?: string) => v === "no" || v === "private";
  if (mode === "driving") {
    if (!DRIVE_CLASSES.has(hw)) return false;
    if (banned(tags.motor_vehicle) || banned(tags.vehicle)) return false;
    return true;
  }
  if (mode === "walking") {
    if (!DRIVE_CLASSES.has(hw) && !WALK_EXTRA.has(hw)) return false;
    if (hw.startsWith("trunk")) return false; // legal-ish but never pleasant
    if (banned(tags.foot)) return false;
    return true;
  }
  // biking (Morgan: "biking keeps biking trails") — cycleways, tracks,
  // paths, plus ANY way explicitly signed for bikes (rail trails are
  // often tagged footway + bicycle=designated).
  const bikeSigned = tags.bicycle === "yes" || tags.bicycle === "designated" ||
    tags.bicycle === "permissive";
  if (!DRIVE_CLASSES.has(hw) && !BIKE_EXTRA.has(hw) && !bikeSigned) return false;
  if (hw.startsWith("trunk")) return false;
  if (banned(tags.bicycle)) return false;
  return true;
}

function distM(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/** CALM-CLASS weighting (Morgan: the loop snapped onto busy NY 9A —
 *  "we want back roads"). "Named" was never the signal; road CLASS is.
 *  Back-road classes compete at face value; secondaries pay a little;
 *  primaries/trunks pay so much they only win when nothing calm exists
 *  within reach. */
export const CLASS_WEIGHT: Record<string, number> = {
  residential: 1, unclassified: 1, tertiary: 1, tertiary_link: 1,
  living_street: 1, track: 1, path: 1, cycleway: 1, footway: 1,
  pedestrian: 1, bridleway: 1, steps: 1.2,
  secondary: 1.3, secondary_link: 1.3,
  primary: 2.6, primary_link: 2.6,
  trunk: 4, trunk_link: 4,
  // Motorways are never eligible for a LOOP waypoint (DRIVE_CLASSES omits
  // them on purpose), but roadResolve does let someone rate one — "I 5 South
  // (Gorgeous)" is a real tag. Weighted like a trunk so a motorway never
  // outranks a calm road that is a similar distance away.
  motorway: 4, motorway_link: 4,
};

/** Nearest eligible point to `wp`, ranked by calm-weighted distance —
 *  a quiet lane 800m away beats a trunk road 300m away. */
export function snapOne(
  wp: LngLat,
  ways: WayEl[],
  mode: Mode,
  maxM = 1500,
): LngLat | null {
  let best: { p: LngLat; score: number } | null = null;
  for (const w of ways) {
    if (w.type !== "way" || !w.tags || !Array.isArray(w.geometry)) continue;
    if (!wayEligible(w.tags, mode)) continue;
    const weight = CLASS_WEIGHT[w.tags.highway ?? ""] ?? 1.5;
    for (const g of w.geometry) {
      if (!g) continue;
      const p: LngLat = [g.lon, g.lat];
      const d = distM(wp, p);
      if (d > maxM) continue;
      const score = d * weight;
      if (!best || score < best.score) best = { p, score };
    }
  }
  return best?.p ?? null;
}

/**
 * Snap every loop waypoint to a mode-legal way. One Overpass call for all
 * waypoints; ~1s on signal. Fail-open to the originals.
 */
export async function snapLoopWaypoints(
  waypoints: LngLat[],
  mode: Mode,
): Promise<LngLat[]> {
  try {
    if (waypoints.length === 0) return waypoints;
    const PAD = 0.008; // ~800 m box per waypoint
    const tiles = waypoints.map(([lng, lat]) => ({
      south: lat - PAD, west: lng - PAD, north: lat + PAD, east: lng + PAD,
    }));
    const { supabase } = await import("@/integrations/supabase/client");
    const { raceTimeout } = await import("@/lib/net");
    const res = await raceTimeout(
      supabase.functions.invoke("overpass", { body: { tiles, kind: "snaproads" } }),
      12_000,
    );
    const data = res && !res.error ? res.data : null;
    const elements: WayEl[] = Array.isArray((data as { elements?: unknown[] } | null)?.elements)
      ? (data as { elements: WayEl[] }).elements
      : [];
    if (elements.length === 0) return waypoints;
    return waypoints.map((wp) => snapOne(wp, elements, mode) ?? wp);
  } catch {
    return waypoints;
  }
}
